import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../common/Table";
import ConfirmationPage from "../../common/ConfirmationPage";
import Model from "../../common/Model";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import {
  getAllFreeMockTest,
  deleteFreeMockTest,
} from "../../redux/services/freeMockTestSlice";
import {
  LuEye,
  LuTrash2,
  LuPhone,
  LuMail,
  LuAward,
  LuClock,
  LuCircleCheck,
  LuCircleX,
  LuUser,
  LuTrendingUp,
  LuPercent,
} from "react-icons/lu";


const FreeMockTest = () => {
  const dispatch = useDispatch();
  const mockTestData = useSelector(
    (state) => state?.freeMockTest?.freeMockTestData
  );


  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchData, setSearchData] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const limitOptions = [
    { value: 10, label: 10 },
    { value: 30, label: 30 },
    { value: 50, label: 50 },
    { value: 100, label: 100 },
  ];
  const [pageLimit, setPageLimit] = useState(limitOptions[0]?.value);

  const fetchRecords = useCallback(async () => {
    try {
      await dispatch(
        getAllFreeMockTest({
          page: page + 1,
          limit: pageLimit,
          searchData,
        })
      );
    } catch (err) {
      console.error(err);
    }
  }, [dispatch, page, pageLimit, searchData]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const listData = useMemo(() => {
    return mockTestData?.mock_test_data || [];
  }, [mockTestData]);

  const totalCount = mockTestData?.totalData || 0;
  const pageLimitCount = mockTestData?.state?.page_limit || 1;

  // Calculate high-level summary cards from visible data
  const stats = useMemo(() => {
    if (!listData.length) {
      return { total: totalCount, avgScore: 0, totalDiscounts: 0, maxScore: 0 };
    }
    const totalScore = listData.reduce((acc, item) => acc + (Number(item.score) || 0), 0);
    const avgScore = (totalScore / listData.length).toFixed(1);
    const totalDiscounts = listData.reduce(
      (acc, item) => acc + (Number(item.earnedDiscount) || 0),
      0
    );
    const maxScore = Math.max(...listData.map((item) => Number(item.score) || 0));
    return {
      total: totalCount,
      avgScore,
      totalDiscounts,
      maxScore: maxScore > 0 ? maxScore : 0,
    };
  }, [listData, totalCount]);

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      const res = await dispatch(deleteFreeMockTest(itemToDelete));
      toast.success(res?.payload?.message || "Mock test record deleted successfully", {
        autoClose: 2000,
        pauseOnHover: false,
      });
      setIsConfirmOpen(false);
      setItemToDelete(null);
      fetchRecords();
    } catch (error) {
      toast.error("Failed to delete record");
    }
  };

  const formatTime = (totalSeconds = 0) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  const columns = [
    {
      header: "S/N",
      key: "index",
      render: (_, __, index) => page * pageLimit + index + 1,
    },
    {
      header: "Candidate",
      key: "name",
      render: (name, row) => (
        <div className="flex flex-col">
          <span className="font-bold text-foreground capitalize flex items-center gap-1.5">
            <span className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-black shrink-0">
              {name ? name.charAt(0).toUpperCase() : "U"}
            </span>
            {name || "Anonymous"}
          </span>
          {row?.subjectTitle && (
            <span className="text-[11px] text-muted-foreground mt-0.5 ml-8">
              {row.subjectTitle}
            </span>
          )}
        </div>
      ),
    },
    {
      header: "Phone Number",
      key: "phone",
      render: (phone) =>
        phone ? (
          <a
            href={`tel:${phone}`}
            className="text-primary hover:underline font-mono text-xs font-semibold flex items-center gap-1"
          >
            <LuPhone className="text-xs text-primary/70 shrink-0" />
            +91 {phone}
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      header: "Email",
      key: "email",
      render: (email) =>
        email ? (
          <span className="text-xs text-foreground/80 flex items-center gap-1">
            <LuMail className="text-xs text-muted-foreground shrink-0" />
            {email}
          </span>
        ) : (
          <span className="text-muted-foreground text-xs">—</span>
        ),
    },
    {
      header: "Score / Total",
      key: "score",
      render: (score, row) => (
        <div className="flex items-center gap-1.5">
          <span
            className={`px-2.5 py-1 rounded-md text-xs font-black ${
              Number(score) > 0
                ? "bg-green-100 text-green-700"
                : Number(score) === 0
                ? "bg-gray-100 text-gray-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {Number(score).toFixed(1)} / {row?.totalMarks || 20}
          </span>
        </div>
      ),
    },
    {
      header: "Performance Breakdown",
      key: "performance",
      render: (_, row) => (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-emerald-600 font-semibold flex items-center gap-0.5" title="Correct">
            ✓ {row?.correctCount ?? 0}
          </span>
          <span className="text-rose-500 font-semibold flex items-center gap-0.5" title="Incorrect">
            ✗ {row?.incorrectCount ?? 0}
          </span>
          <span className="text-amber-500 font-semibold flex items-center gap-0.5" title="Unattempted">
            ○ {row?.unattemptedCount ?? 0}
          </span>
        </div>
      ),
    },
    {
      header: "Time Spent",
      key: "timeTakenSeconds",
      render: (sec) => (
        <span className="text-xs text-foreground/80 flex items-center gap-1 font-mono">
          <LuClock className="text-xs text-muted-foreground" />
          {formatTime(Number(sec) || 0)}
        </span>
      ),
    },
    {
      header: "Discount Earned",
      key: "earnedDiscount",
      render: (discount) =>
        Number(discount) > 0 ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold">
            <LuAward className="text-xs text-emerald-600" />₹{Number(discount).toLocaleString()} OFF
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">None</span>
        ),
    },
    {
      header: "Submitted At",
      key: "createdAt",
      render: (date) =>
        date ? (
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {moment(date).tz("Asia/Kolkata").format("DD MMM YYYY, hh:mm A")}
          </span>
        ) : (
          "—"
        ),
    },
  ];

  const actions = [
    {
      icon: <LuEye className="w-5 h-5 text-primary hover:text-primary/80" />,
      onClick: (row) => {
        setSelectedRecord(row);
        setIsDetailOpen(true);
      },
    },
    {
      icon: <LuTrash2 className="w-5 h-5 text-red-500 hover:text-red-700" />,
      onClick: (row) => {
        setItemToDelete(row._id);
        setIsConfirmOpen(true);
      },
    },
  ];

  return (
    <div className="space-y-4">
      {/* Top Stat Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Card 1 */}
        <div className="bg-card rounded-xl border border-border p-4 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
              Total Test Takers
            </p>
            <p className="text-2xl font-black text-primary mt-1">{stats.total}</p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center text-xl">
            <LuUser />
          </div>
        </div>

        {/* Card 2 */}
        <div className="bg-card rounded-xl border border-border p-4 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
              Average Score
            </p>
            <p className="text-2xl font-black text-foreground mt-1">
              {stats.avgScore}{" "}
              <span className="text-xs font-normal text-muted-foreground">pts</span>
            </p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl">
            <LuTrendingUp />
          </div>
        </div>

        {/* Card 3 */}
        <div className="bg-card rounded-xl border border-border p-4 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
              Highest Score
            </p>
            <p className="text-2xl font-black text-foreground mt-1">
              {stats.maxScore}{" "}
              <span className="text-xs font-normal text-muted-foreground">pts</span>
            </p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-xl">
            <LuAward />
          </div>
        </div>

        {/* Card 4 */}
        <div className="bg-card rounded-xl border border-border p-4 shadow-xs flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
              Discounts Unlocked
            </p>
            <p className="text-2xl font-black text-emerald-600 mt-1">
              ₹{stats.totalDiscounts.toLocaleString()}
            </p>
          </div>
          <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl">
            <LuPercent />
          </div>
        </div>
      </div>

      {/* Main Table */}
      <Table
        title="Free Mock Test Submissions"
        showSearchbar={true}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        setSearchData={setSearchData}
        columns={columns}
        data={listData}
        actions={actions}
        pageCount={pageLimitCount}
        onPageChange={(selectedItem) => setPage(selectedItem.selected)}
        pageLimitOptions={limitOptions}
        selectedPageLimit={pageLimit}
        onPageLimitChange={setPageLimit}
      />

      {/* Detail Modal */}
      {isDetailOpen && selectedRecord && (
        <Model
          title="Mock Test Submission Details"
          closeModal={() => {
            setIsDetailOpen(false);
            setSelectedRecord(null);
          }}
          isForm={false}
        >
          <div className="space-y-5 text-sm">
            {/* Header / Student Badge */}
            <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-xs uppercase font-extrabold text-primary tracking-wider">
                  Candidate Info
                </p>
                <h3 className="text-lg font-black text-foreground mt-0.5">
                  {selectedRecord?.name || "Anonymous Candidate"}
                </h3>
                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1 font-mono">
                    <LuPhone className="text-primary" /> +91 {selectedRecord?.phone || "—"}
                  </span>
                  {selectedRecord?.email && (
                    <span className="flex items-center gap-1">
                      <LuMail className="text-primary" /> {selectedRecord.email}
                    </span>
                  )}
                </div>
              </div>

              {selectedRecord?.earnedDiscount > 0 && (
                <div className="px-3.5 py-2 rounded-xl bg-emerald-100 text-emerald-800 border border-emerald-300 font-extrabold text-center sm:text-right shrink-0">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700">Merit Reward</p>
                  <p className="text-base font-black">
                    ₹{Number(selectedRecord.earnedDiscount).toLocaleString()} Discount
                  </p>
                </div>
              )}
            </div>

            {/* Scorecard Metrics Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 rounded-xl bg-card border border-border">
                <p className="text-[11px] font-bold text-muted-foreground uppercase">Final Score</p>
                <p className="text-xl font-extrabold text-primary mt-1">
                  {Number(selectedRecord?.score).toFixed(1)} / {selectedRecord?.totalMarks || 20}
                </p>
              </div>

              <div className="p-3 rounded-xl bg-card border border-border">
                <p className="text-[11px] font-bold text-muted-foreground uppercase">Correct Answers</p>
                <p className="text-xl font-extrabold text-emerald-600 mt-1 flex items-center gap-1">
                  <LuCircleCheck className="text-base" /> {selectedRecord?.correctCount ?? 0}
                </p>
              </div>

              <div className="p-3 rounded-xl bg-card border border-border">
                <p className="text-[11px] font-bold text-muted-foreground uppercase">Incorrect</p>
                <p className="text-xl font-extrabold text-rose-600 mt-1 flex items-center gap-1">
                  <LuCircleX className="text-base" /> {selectedRecord?.incorrectCount ?? 0}
                </p>
              </div>


              <div className="p-3 rounded-xl bg-card border border-border">
                <p className="text-[11px] font-bold text-muted-foreground uppercase">Unattempted</p>
                <p className="text-xl font-extrabold text-amber-600 mt-1">
                  {selectedRecord?.unattemptedCount ?? 0}
                </p>
              </div>
            </div>

            {/* Additional Info Table */}
            <div className="rounded-xl border border-border overflow-hidden">
              <table className="w-full text-xs">
                <tbody>
                  <tr className="border-b border-border bg-muted/20">
                    <td className="px-4 py-2.5 font-bold text-muted-foreground w-1/3">Subject</td>
                    <td className="px-4 py-2.5 font-medium text-foreground">
                      {selectedRecord?.subjectTitle || "UPSC Multi-Subject Prelims"}
                    </td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="px-4 py-2.5 font-bold text-muted-foreground">Series / Exam</td>
                    <td className="px-4 py-2.5 font-medium text-foreground">
                      {selectedRecord?.seriesTitle || "Achiwon mock series"}
                    </td>
                  </tr>
                  <tr className="border-b border-border bg-muted/20">
                    <td className="px-4 py-2.5 font-bold text-muted-foreground">Total Questions</td>
                    <td className="px-4 py-2.5 font-medium text-foreground">
                      {selectedRecord?.totalQuestions || 10} Questions
                    </td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="px-4 py-2.5 font-bold text-muted-foreground">Time Spent</td>
                    <td className="px-4 py-2.5 font-medium text-foreground font-mono">
                      {formatTime(Number(selectedRecord?.timeTakenSeconds) || 0)}
                    </td>
                  </tr>
                  <tr className="border-b border-border bg-muted/20">
                    <td className="px-4 py-2.5 font-bold text-muted-foreground">Percentage / Accuracy</td>
                    <td className="px-4 py-2.5 font-medium text-foreground">
                      {selectedRecord?.percentage ? `${selectedRecord.percentage}%` : "—"} /{" "}
                      {selectedRecord?.accuracy ? `${selectedRecord.accuracy}% accuracy` : "—"}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2.5 font-bold text-muted-foreground">Submission Date & Time</td>
                    <td className="px-4 py-2.5 font-medium text-foreground">
                      {selectedRecord?.createdAt
                        ? moment(selectedRecord.createdAt)
                            .tz("Asia/Kolkata")
                            .format("DD MMMM YYYY, hh:mm:ss A [IST]")
                        : "—"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Quick WhatsApp Action Button */}
            {selectedRecord?.phone && (
              <div className="pt-2 flex justify-end">
                <a
                  href={`https://wa.me/91${selectedRecord.phone}?text=${encodeURIComponent(
                    `Hello ${selectedRecord.name || ""}, thank you for attempting our Free UPSC Mock Test! Your score was ${selectedRecord.score}/${selectedRecord.totalMarks || 20}.`
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1.5 transition"
                >
                  <LuPhone className="text-sm" /> Chat on WhatsApp
                </a>
              </div>
            )}
          </div>
        </Model>
      )}

      {/* Delete Confirmation */}
      {isConfirmOpen && (
        <ConfirmationPage
          topicName="Mock Test Record"
          confirm={isConfirmOpen}
          onDelete={handleDelete}
          onCancel={() => {
            setIsConfirmOpen(false);
            setItemToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default FreeMockTest;
